import React, { useState, useCallback, useEffect, useContext } from 'react';
import styled, { css } from 'styled-components';
import { Trans } from 'react-i18next';
import * as api from '@ubeya/shared/services/api';
import { ALLOWED_PHOTO_TYPES } from '@ubeya/shared/constants';
import { flexCenter } from '../Flex';
import { small } from '../Typography';
import { Tooltip } from '../Tooltip';
import { ReactComponent as FileIcon } from '../../assets/camera.svg';
import ConfigContext from '../../contexts/ConfigContext';
import BaseFileUpload from './FileUpload';
import ImageCrop from './ImageCrop';

const FileUpload = styled(BaseFileUpload)`
  position: relative;
  ${({ showImagePlaceholder, isDashboard }) =>
    showImagePlaceholder && isDashboard
      ? css`
          position: relative;
          cursor: ${({ enabled }) => (enabled ? 'pointer' : 'default')};
          ${flexCenter};
          font-size: 14px;
          font-weight: 500;
          line-height: 19px;
          border-radius: 100%;
          width: 100%;
          height: 100%;
          background: ${({ theme }) => theme.colors.grey1};

          &&& {
            background-image: url(${({ image }) => image});
            background-size: cover;
            background-position: center;
          }
          color: ${({ theme }) => theme.colors.black};
          text-align: center;
          outline: 2px solid ${({ theme }) => theme.colors.white};
          outline-offset: -4px;

          &:focus {
            border-color: ${({ theme }) => theme.colors.primary};
          }
        `
      : showImagePlaceholder &&
        css`
          position: relative;
          cursor: ${({ enabled }) => (enabled ? 'pointer' : 'default')};
          ${flexCenter};
          ${small};
          border-radius: 100%;
          width: 150px;
          height: 150px;
          min-height: 150px;
          background: ${({ theme }) => theme.colors.white};

          &&& {
            background-image: url(${({ image }) => image});
            background-size: cover;
            background-position: center;
          }
          color: ${({ theme }) => theme.colors.grey3};
          border: 2px solid ${({ theme }) => theme.colors.grey3};
          text-align: center;
          outline: none;

          &:focus {
            border-color: ${({ theme }) => theme.colors.primary};
          }
        `}
`;

const UploadBackground = styled.div.attrs(() => ({ className: 'image-upload-background' }))`
  position: absolute;
  bottom: 0;
  right: 0;
  background: ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  width: 45px;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 5px solid ${({ theme }) => theme.colors.white};
  transition: background 200ms;
  &:hover {
    background: ${({ theme }) => theme.colors.primary};
  }
`;

const UploadIcon = styled(FileIcon)`
  width: 20px;
  height: 20px;
  margin-top: -4px;
`;

const defaultDataSetter = (value) => value;
const defaultDataGetter = (value) => value.path;

const ImageUpload = ({
  value,
  onChange,
  children,
  dataSetter = defaultDataSetter,
  dataGetter = defaultDataGetter,
  overImageComponent = undefined,
  className,
  showUploadIcon = true,
  cropBeforeUpload = true,
  showImagePlaceholder = true,
  alwaysDisplayText = false,
  enabled = true,
  isDashboard = false,
  cropAspect,
  squareCrop,
  dropzoneRef = null
}) => {
  const { t } = useContext(ConfigContext);
  const [image, setImage] = useState(dataSetter(value));
  const [cropModal, setCropModal] = useState(false);

  const onDrop = useCallback(
    (file) => {
      if (cropBeforeUpload) {
        const fileReader = new FileReader();
        fileReader.onloadend = () => {
          setCropModal(fileReader.result);
        };
        fileReader.readAsDataURL(file);
      } else {
        setImage(URL.createObjectURL(file));
      }
    },
    [cropBeforeUpload]
  );

  const onUploaded = useCallback((data, file) => onChange(dataGetter(data), file && URL.createObjectURL(file)), [
    dataGetter,
    onChange
  ]);

  useEffect(() => {
    if (value?.includes?.('http')) {
      setImage(dataSetter(value));
    }
  }, [dataSetter, value]);

  return (
    <>
      <FileUpload
        className={className}
        showImagePlaceholder={showImagePlaceholder}
        enabled={enabled}
        image={image}
        apiFunc={cropBeforeUpload ? undefined : api.uploadImage}
        onUploaded={onUploaded}
        onDrop={onDrop}
        accept={ALLOWED_PHOTO_TYPES}
        isDashboard={isDashboard}
        multiple={false}
        dropzoneRef={dropzoneRef}>
        {(!image || alwaysDisplayText) &&
          (children ?? (isDashboard ? <Trans>choosePhoto</Trans> : <Trans>addPhoto</Trans>))}
        {showUploadIcon && (
          <Tooltip body={t('upload')}>
            <UploadBackground>
              <UploadIcon fill="white" />
            </UploadBackground>
          </Tooltip>
        )}
        {image && overImageComponent}
      </FileUpload>
      {cropModal && (
        <ImageCrop
          squareCrop={squareCrop}
          aspect={cropAspect}
          image={cropModal}
          onClose={() => setCropModal()}
          onConfirm={async (confirmedImage) => {
            const { data } = await api.uploadImage([confirmedImage]);
            setImage(URL.createObjectURL(confirmedImage));
            onUploaded(data, confirmedImage);
          }}
        />
      )}
    </>
  );
};

export default ImageUpload;
